import { collection, query, where, limit, getDocs, doc, getDoc, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getItemStockCached } from './stockFlowHelper';
import { getCustomersCached } from './masterDataStore';
import { twoUnitsModuleOn, getTwoUnitPair, roundBaseQty } from './unitConversion';

// ── Format Validations ──────────────────────────────────────────────

export const isValidEmail = (email) => {
  if (!email || !email.trim()) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

export const isValidPhone = (phone) => {
  if (!phone || !phone.trim()) return true;
  return /^[\+\d][\d\s\-()]{6,15}$/.test(phone.trim());
};

export const isValidGST = (gst) => {
  if (!gst || !gst.trim()) return true;
  return /^[\dA-Za-z\-/]{5,20}$/.test(gst.trim());
};

// ── Password Strength ───────────────────────────────────────────────

// ── Stock Availability ──────────────────────────────────────────────

// Convert an entered quantity to BASE units using the item's units[] table.
// Shared by the stock check and by callers that decrement a cached stock map.
export function toBaseQty(itemData, quantity, unitCode) {
  const itmUnits = Array.isArray(itemData?.units) ? itemData.units : [];
  const baseRow = itmUnits.find(u => u.isBase) || itmUnits[0] || null;
  const baseUnitCode = baseRow?.unitCode || itemData?.unitOfMeasure || '';
  const enteredUnitCode = unitCode || baseUnitCode;
  const unitRow = itmUnits.find(u => u.unitCode === enteredUnitCode);
  const conversion = Number(unitRow?.conversion) || 1;
  let requestedBase = (Number(quantity) || 0) * conversion;
  // Show Two Units: 10.416666667 Ctn × 24 = 250.000000008 → 250 Mtr, so a
  // qty typed in the second unit never shows a false "not enough stock".
  if (twoUnitsModuleOn() && getTwoUnitPair(itemData)) requestedBase = roundBaseQty(requestedBase);
  return { requestedBase, conversion, enteredUnitCode };
}

/**
 * Check whether there's enough stock for each line before posting.
 *
 * Firebase-cost note: passing `opts.stockMap` (itemId → on-hand in BASE units)
 * AND `opts.itemsMap` (itemId → item record) lets the caller REUSE data it has
 * already loaded (e.g. a form's cached stock badges + cached items), skipping
 * the per-item stockFlow + item reads entirely. When those aren't provided it
 * reads live from Firestore exactly as before — so every existing caller is
 * unchanged. (`stockMap` is branch/FY/status-scoped like the on-screen badge,
 * which is the number the user is actually looking at.)
 */
export async function checkStockAvailability(companyId, items, opts = {}) {
  const { stockMap = null, itemsMap = null, branchId = null } = opts;
  const validItems = items.filter(item => item.itemId && item.quantity > 0);
  if (validItems.length === 0) return [];

  const results = await Promise.all(validItems.map(async (item) => {
    let itemData, availableBase;

    // Reuse is decided PER ITEM, not all-or-nothing. Stock badges are now
    // on-demand, so the map holds only the items the user actually looked at —
    // treating a missing entry as "0 in stock" (the old all-or-nothing branch)
    // would have made every Post report false "insufficient stock".
    const known = stockMap && stockMap[item.itemId] !== undefined && stockMap[item.itemId] !== null;
    itemData = (itemsMap && itemsMap[item.itemId]) || null;

    if (known) {
      availableBase = Number(stockMap[item.itemId]) || 0;
    } else {
      // Read just THIS item's on-hand figure (cached for the session). Scoped
      // to active statuses + active financial year, i.e. the same number the
      // stock badge shows — the previous live path counted every row ever,
      // including Draft and Cancelled, so the two could disagree.
      availableBase = await getItemStockCached(companyId, item.itemId, { branchId });
    }

    if (!itemData) {
      // Item record only needed for unit conversion / service flag.
      const itemSnap = await getDoc(doc(db, 'companies', companyId, 'inventoryItems', item.itemId));
      itemData = itemSnap.exists() ? itemSnap.data() : {};
    }

    // Service items don't carry stock — never warn.
    if (itemData.itemType === 'service') return null;

    const { requestedBase, conversion, enteredUnitCode } =
      toBaseQty(itemData, item.quantity, item.unitCode || item.unitOfMeasure);

    if (availableBase < requestedBase && known) {
      // The figure came from a cached/in-memory map, which can lag behind stock
      // received on another device. NEVER warn or block on a stale number —
      // re-read this one item live before deciding. Costs a read only when
      // there appears to be a shortage, which is rare, and prevents a till
      // refusing to sell goods that were booked in minutes ago.
      try {
        availableBase = await getItemStockCached(companyId, item.itemId, { forceRefresh: true });
      } catch { /* keep the cached figure if the re-check fails */ }
    }

    if (availableBase < requestedBase) {
      // Show numbers in the entered unit so the message is meaningful to the user.
      const availableEntered = conversion > 0 ? availableBase / conversion : availableBase;
      return {
        itemName: item.itemName || 'Unknown Item',
        available: Math.max(0, Math.round(availableEntered * 100) / 100),
        requested: item.quantity,
        unit: enteredUnitCode || '',
      };
    }
    return null;
  }));

  return results.filter(Boolean);
}

// ── Customer Credit Limit ───────────────────────────────────────────

export async function checkCustomerCreditLimit(companyId, customerId, newAmount, excludeTransactionId) {
  // Speed: most customers have no credit limit — decide that from the device's
  // customer list (no server trip). Only customers WITH a limit are checked live.
  try {
    const cached = await getCustomersCached(companyId);
    const c = (cached || []).find(x => x.id === customerId);
    if (c && !(Number(c.creditLimit) > 0)) return { exceeded: false };
  } catch (e) { /* fall through to the live check */ }

  const customerDoc = await getDoc(doc(db, 'companies', companyId, 'customers', customerId));
  if (!customerDoc.exists()) return { exceeded: false };

  const customer = customerDoc.data();
  const creditLimit = Number(customer.creditLimit) || 0;
  if (creditLimit <= 0) return { exceeded: false };

  const invoiceQuery = query(
    collection(db, 'companies', companyId, 'transactions'),
    where('partyId', '==', customerId),
    where('tranTypeCode', '==', 'SI')
  );
  const snapshot = await getDocs(invoiceQuery);

  let outstanding = 0;
  snapshot.docs.forEach((d) => {
    if (d.id === excludeTransactionId) return;
    const data = d.data();
    if (data.status === 'Draft' || data.status === 'Cancelled') return;
    outstanding += Number(data.balanceAmount ?? data.totalAmount ?? 0);
  });

  const newTotal = outstanding + Number(newAmount);
  if (newTotal > creditLimit) {
    return { exceeded: true, creditLimit, outstanding, newAmount: Number(newAmount), newTotal };
  }
  return { exceeded: false };
}

// ── Duplicate Vendor Invoice ────────────────────────────────────────

export async function checkDuplicateVendorInvoice(companyId, vendorId, vendorInvoiceNo, excludeTransactionId) {
  if (!vendorInvoiceNo || !vendorInvoiceNo.trim()) return false;

  const q = query(
    collection(db, 'companies', companyId, 'transactions'),
    where('partyId', '==', vendorId),
    where('vendorInvoiceNo', '==', vendorInvoiceNo.trim()),
    limit(2)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter((d) => d.id !== excludeTransactionId).length > 0;
}

// ── Duplicate Document Number ───────────────────────────────────────

// ── Financial Year Overlap ──────────────────────────────────────────

// ── Open Draft Transactions in FY ───────────────────────────────────

// ── Future Date Check ───────────────────────────────────────────────

export function isFutureDate(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date > today;
}

// ── Account Code Uniqueness ─────────────────────────────────────────

export async function isAccountCodeDuplicate(companyId, code, excludeAccountId) {
  if (!code || !code.trim()) return false;

  const q = query(
    collection(db, 'companies', companyId, 'accounts'),
    where('code', '==', code.trim()),
    limit(2)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter((d) => d.id !== excludeAccountId).length > 0;
}

// ── Transaction Reference Uniqueness ────────────────────────────────
// Returns the doc numbers of OTHER transactions of the same type that already
// use this reference (empty array = none). Used to warn on duplicate JV
// references. One small scoped query (only when a reference is entered), so the
// Firebase read cost is negligible and paid only on save.
export async function findTransactionsByReference(companyId, tranTypeCode, reference, excludeTransactionId) {
  const ref = (reference ?? '').toString().trim();
  if (!companyId || !ref) return [];
  const q = query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', tranTypeCode),
    where('reference', '==', ref),
    limit(5)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .filter((d) => d.id !== excludeTransactionId)
    .map((d) => d.data().docNumber || d.id);
}

// ── Item Code Uniqueness ────────────────────────────────────────────

export async function isItemCodeDuplicate(companyId, itemCode, excludeItemId) {
  if (!itemCode || !itemCode.trim()) return false;

  const q = query(
    collection(db, 'companies', companyId, 'inventoryItems'),
    where('itemCode', '==', itemCode.trim()),
    limit(2)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter((d) => d.id !== excludeItemId && d.data().isDeleted !== true).length > 0;
}

// ── Audit Trail ─────────────────────────────────────────────────────

export async function writeAuditLog(companyId, userId, action, entityType, entityId, details) {
  try {
    await addDoc(collection(db, 'companies', companyId, 'auditLog'), {
      action,
      entityType,
      entityId,
      performedBy: userId || 'unknown',
      details: details || '',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Failed to write audit log:', error);
  }
}

// ── Large Transaction Warning ───────────────────────────────────────

export const LARGE_TRANSACTION_THRESHOLD = 10000000;

export function isLargeTransaction(amount) {
  return Number(amount) >= LARGE_TRANSACTION_THRESHOLD;
}

// ── Payment Terms Overdue Check ─────────────────────────────────────

export async function checkPartyOverdue(companyId, partyId, partyType) {
  const tranTypeCode = partyType === 'customer' ? 'SI' : 'PI';

  const q = query(
    collection(db, 'companies', companyId, 'transactions'),
    where('partyId', '==', partyId),
    where('tranTypeCode', '==', tranTypeCode)
  );
  const snapshot = await getDocs(q);

  const today = new Date();
  let overdueCount = 0;
  let overdueAmount = 0;

  snapshot.docs.forEach((d) => {
    const data = d.data();
    if (data.status === 'Draft' || data.status === 'Cancelled' || data.status === 'Paid') return;
    const balance = Number(data.balanceAmount ?? data.totalAmount ?? 0);
    if (balance <= 0) return;

    const docDate = new Date(data.docDate);
    const paymentTerms = Number(data.paymentTerms) || 30;
    const dueDate = new Date(docDate);
    dueDate.setDate(dueDate.getDate() + paymentTerms);

    if (today > dueDate) {
      overdueCount++;
      overdueAmount += balance;
    }
  });

  return { overdueCount, overdueAmount };
}
